import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
} from 'recharts';
import { ReactComponent as ThreePeople } from '../../components/icons/people.svg';

const normalizeMiniValue = (value) =>
    typeof value === 'number' ? { percent: value, count: null } : value;
const toMiniPercent = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.min(100, Math.round(parsed)));
};
const formatMiniCount = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '-';
    return parsed.toLocaleString('pl-PL');
};

export const formatStatNumber = (value) => (Number(value) || 0).toLocaleString('pl-PL');

export const PercentLabel = ({ x, y, width, height, value, payload }) => {
    if (!payload) return null;
    return (
        <text x={x + width + 8} y={y + height / 2 + 4} fill="#2F5F6D" fontSize={12}>
            {`${value}% (${payload.count})`}
        </text>
    );
};

export const PercentTooltip = ({ active, payload, labelText = 'użytkowników' }) => {
    if (!active || !payload?.length) return null;
    const { count, percent } = payload[0].payload;
    return (
        <div className="stats-tooltip">
            <div className="stats-tooltip__value">{percent}%</div>
            <div className="stats-tooltip__hint">{count} {labelText}</div>
        </div>
    );
};

export const ConfirmedTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const { value, confirmed, total } = payload[0].payload;
    return (
        <div className="stats-tooltip">
            <div className="stats-tooltip__title">{label}</div>
            <div className="stats-tooltip__value">{value}%</div>
            <div className="stats-tooltip__hint">{confirmed} / {total} wyjść</div>
        </div>
    );
};

export const GenderTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { label, percent, count } = payload[0].payload;

    return (
        <div className="stats-tooltip">
            <div className="stats-tooltip__title">{label}</div>
            <div className="stats-tooltip__value">{percent}%</div>
            <div className="stats-tooltip__hint">{count.toLocaleString('pl-PL')} użytkowników</div>
        </div>
    );
};

const CALENDAR_WEEKDAY_SHORT = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];

const buildCalendarDays = (viewMonthDate) => {
    const year = viewMonthDate.getUTCFullYear();
    const month = viewMonthDate.getUTCMonth();
    const firstDayOfMonth = new Date(Date.UTC(year, month, 1));
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const daysInPreviousMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const leadingDays = (firstDayOfMonth.getUTCDay() + 6) % 7;
    const cells = [];

    for (let index = leadingDays; index > 0; index -= 1) {
        const day = daysInPreviousMonth - index + 1;
        cells.push({
            date: new Date(Date.UTC(year, month - 1, day)),
            isCurrentMonth: false,
        });
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
        cells.push({
            date: new Date(Date.UTC(year, month, day)),
            isCurrentMonth: true,
        });
    }

    const trailingDays = 42 - cells.length;
    for (let day = 1; day <= trailingDays; day += 1) {
        cells.push({
            date: new Date(Date.UTC(year, month + 1, day)),
            isCurrentMonth: false,
        });
    }

    return cells;
};

export const DateRangePicker = ({
    startValue,
    endValue,
    onChange,
    monthOptions,
    parseDateValue,
    normalizeDateRangeValues,
    formatDatePl,
    toDateInputValue,
    toUtcDayStamp,
    isSameUtcDate,
}) => {
    const pickerRef = useRef(null);
    const wasOpenRef = useRef(false);
    const rawStartDate = useMemo(() => parseDateValue(startValue), [parseDateValue, startValue]);
    const rawEndDate = useMemo(() => parseDateValue(endValue), [parseDateValue, endValue]);
    const normalizedRange = normalizeDateRangeValues(startValue, endValue);
    const { startDate, endDate } = normalizedRange;

    const [isOpen, setIsOpen] = useState(false);
    const [viewMonthDate, setViewMonthDate] = useState(() => {
        const seedDate = rawStartDate || new Date();
        return new Date(Date.UTC(seedDate.getUTCFullYear(), seedDate.getUTCMonth(), 1));
    });

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleOutsideClick = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen]);

    useEffect(() => {
        const justOpened = isOpen && !wasOpenRef.current;

        if (justOpened) {
            const seedDate = rawStartDate || rawEndDate || new Date();
            setViewMonthDate(new Date(Date.UTC(seedDate.getUTCFullYear(), seedDate.getUTCMonth(), 1)));
        }

        wasOpenRef.current = isOpen;
    }, [isOpen, rawStartDate, rawEndDate]);

    const triggerLabel = useMemo(() => {
        if (rawStartDate && rawEndDate) {
            return `${formatDatePl(startDate)} - ${formatDatePl(endDate)}`;
        }
        if (rawStartDate) {
            return `${formatDatePl(rawStartDate)} - ...`;
        }
        return 'Wybierz zakres dat';
    }, [rawStartDate, rawEndDate, startDate, endDate, formatDatePl]);

    const handleSelectDay = (selectedDate) => {
        const selectedValue = toDateInputValue(selectedDate);
        const selectedStamp = toUtcDayStamp(selectedDate);

        if (!rawStartDate || rawEndDate) {
            onChange(selectedValue, '');
            setViewMonthDate(new Date(Date.UTC(selectedDate.getUTCFullYear(), selectedDate.getUTCMonth(), 1)));
            return;
        }

        const rawStartStamp = toUtcDayStamp(rawStartDate);

        if (selectedStamp < rawStartStamp) {
            onChange(selectedValue, startValue);
        } else {
            onChange(startValue, selectedValue);
        }

        setIsOpen(false);
    };

    const goToPreviousMonth = () => {
        setViewMonthDate((previousMonth) => (
            new Date(Date.UTC(previousMonth.getUTCFullYear(), previousMonth.getUTCMonth() - 1, 1))
        ));
    };

    const goToNextMonth = () => {
        setViewMonthDate((previousMonth) => (
            new Date(Date.UTC(previousMonth.getUTCFullYear(), previousMonth.getUTCMonth() + 1, 1))
        ));
    };

    const handleMonthWheel = (event) => {
        event.preventDefault();

        if (event.deltaY > 0) {
            goToNextMonth();
            return;
        }

        if (event.deltaY < 0) {
            goToPreviousMonth();
        }
    };

    const startStamp = startDate ? toUtcDayStamp(startDate) : null;
    const endStamp = endDate ? toUtcDayStamp(endDate) : null;

    return (
        <div className="stats-date-range" ref={pickerRef}>
            <button
                type="button"
                className={`stats-date-range-trigger ${isOpen ? 'is-open' : ''} ${rawStartDate ? '' : 'is-empty'}`}
                onClick={() => setIsOpen((previous) => !previous)}
            >
                <span>{triggerLabel}</span>
                <span className="stats-date-range-trigger-arrow">▼</span>
            </button>

            {isOpen && (
                <div className="stats-date-range-popover" onWheel={handleMonthWheel}>
                    <div className="stats-date-range-header">
                        <button type="button" className="stats-date-range-nav" onClick={goToPreviousMonth}>
                            ‹
                        </button>
                        <div className="stats-date-range-panel-title">
                            Zakres dat
                        </div>
                        <button type="button" className="stats-date-range-nav" onClick={goToNextMonth}>
                            ›
                        </button>
                    </div>

                    <div className="stats-date-range-title">
                        {monthOptions[viewMonthDate.getUTCMonth()].label} {viewMonthDate.getUTCFullYear()}
                    </div>
                    <div className="stats-date-range-weekdays">
                        {CALENDAR_WEEKDAY_SHORT.map((label) => (
                            <span key={label} className="stats-date-range-weekday">{label}</span>
                        ))}
                    </div>

                    <div className="stats-date-range-grid">
                        {buildCalendarDays(viewMonthDate).map((cell) => {
                            const dayStamp = toUtcDayStamp(cell.date);
                            const isStart = isSameUtcDate(cell.date, startDate);
                            const isEnd = isSameUtcDate(cell.date, endDate);
                            const isBetween =
                                startStamp != null &&
                                endStamp != null &&
                                dayStamp > startStamp &&
                                dayStamp < endStamp;

                            const classes = [
                                'stats-date-range-day',
                                !cell.isCurrentMonth ? 'stats-date-range-day--outside' : '',
                                isBetween ? 'stats-date-range-day--in-range' : '',
                                isStart ? 'stats-date-range-day--start' : '',
                                isEnd ? 'stats-date-range-day--end' : '',
                            ]
                                .filter(Boolean)
                                .join(' ');

                            return (
                                <button
                                    key={toDateInputValue(cell.date)}
                                    type="button"
                                    className={classes}
                                    onClick={() => handleSelectDay(cell.date)}
                                >
                                    {cell.date.getUTCDate()}
                                </button>
                            );
                        })}
                    </div>

                    <div className="stats-date-range-footer">
                        <div className="stats-date-range-hint">
                            {rawStartDate && !rawEndDate
                                ? 'Wybierz datę końcową'
                                : 'Wybierz datę początkową i końcową'}
                        </div>
                        {(rawStartDate || rawEndDate) && (
                            <button
                                type="button"
                                className="stats-date-range-clear"
                                onClick={() => onChange('', '')}
                            >
                                Wyczyść
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export const SummaryGrid = ({ items, className = '' }) => (
    <div className={`stats-summary-grid ${className}`.trim()}>
        {items.map((item) => (
            <div key={item.key} className="stats-summary-card">
                <div className="stats-summary-value">{item.value}</div>
                <div className="stats-summary-label">{item.label}</div>
                <div className="stats-summary-note">{item.note}</div>
            </div>
        ))}
    </div>
);

export const MiniDemography = ({ gender, ages }) => (
    <div className="stats-mini-demography">
        <div className="stats-mini-block">
            <div className="stats-mini-title">Płeć</div>
            {Object.entries(gender).map(([key, rawValue]) => {
                const { percent, count } = normalizeMiniValue(rawValue);
                const percentValue = toMiniPercent(percent);
                return (
                    <div key={key} className="stats-mini-row">
                        <span className="stats-mini-label">{key}</span>
                        <div
                            className="stats-mini-bar"
                            title={`${percentValue}%`}
                            tabIndex={0}
                            style={{ '--mini-percent': `${percentValue}%` }}
                            aria-label={`Udział: ${percentValue}%`}
                        >
                            <span className="stats-mini-bar-fill" style={{ width: `${percentValue}%` }} />
                            <span className="stats-mini-bar-tooltip">{percentValue}%</span>
                        </div>
                        <span className="stats-mini-value">{formatMiniCount(count)}</span>
                    </div>
                );
            })}
        </div>
        <div className="stats-mini-block">
            <div className="stats-mini-title">Wiek</div>
            {ages.map((item) => {
                const percentValue = toMiniPercent(item.percent);

                return (
                    <div key={item.range} className="stats-mini-row">
                        <span className="stats-mini-label">{item.range}</span>
                        <div
                            className="stats-mini-bar"
                            title={`${percentValue}%`}
                            tabIndex={0}
                            style={{ '--mini-percent': `${percentValue}%` }}
                            aria-label={`Udział: ${percentValue}%`}
                        >
                            <span className="stats-mini-bar-fill" style={{ width: `${percentValue}%` }} />
                            <span className="stats-mini-bar-tooltip">{percentValue}%</span>
                        </div>
                        <span className="stats-mini-value">{formatMiniCount(item.count)}</span>
                    </div>
                );
            })}
        </div>
    </div>
);

export const EventToggleButton = ({
    label,
    color,
    active,
    onClick,
}) => (
    <button
        type="button"
        className={`stats-event-toggle-pill ${active ? 'is-active' : ''}`}
        style={{ backgroundColor: color }}
        onClick={onClick}
    >
        <span className={`stats-event-toggle-box ${active ? 'is-active' : ''}`}>
            {active ? '✓' : ''}
        </span>
        <span>{label}</span>
    </button>
);

export const EventDonutCard = ({ title, items, onInfoClick }) => {
    const safeItems = Array.isArray(items) ? items : [];
    const total = safeItems.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
    const [activeItemKey, setActiveItemKey] = useState(null);
    const activeItem = safeItems.find((item) => item.key === activeItemKey) || null;
    const centerValue = activeItem ? Number(activeItem.count) || 0 : total;

    return (
        <div className="stats-event-kpi-card">
            <div className="stats-event-kpi-header">
                <div className="stats-event-kpi-title-wrap">
                    <ThreePeople className="stats-event-kpi-icon" />
                    <div className="stats-event-kpi-title">{title}</div>
                </div>
                <button
                    type="button"
                    className="stats-event-kpi-info"
                    aria-label={`Informacja: ${title}`}
                    onClick={onInfoClick}
                >
                    i
                </button>
            </div>

            <div className="stats-event-kpi-body">
                {total > 0 ? (
                    <div className="stats-event-kpi-donut-wrap">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={safeItems}
                                    dataKey="count"
                                    nameKey="label"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={52}
                                    outerRadius={76}
                                    paddingAngle={1.5}
                                    stroke="none"
                                    startAngle={90}
                                    endAngle={-270}
                                    onMouseEnter={(_, index) => setActiveItemKey(safeItems[index]?.key || null)}
                                    onMouseLeave={() => setActiveItemKey(null)}
                                >
                                    {safeItems.map((item) => (
                                        <Cell
                                            key={`event-kpi-${title}-${item.key}`}
                                            fill={item.color}
                                            fillOpacity={activeItemKey && activeItemKey !== item.key ? 0.45 : 1}
                                        />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <div
                            className="stats-event-kpi-center-value"
                            style={{ color: activeItem?.color || '#7D959F' }}
                        >
                            {formatStatNumber(centerValue)}
                        </div>
                    </div>
                ) : (
                    <div className="stats-event-kpi-empty">{formatStatNumber(total)}</div>
                )}
            </div>

            <div className={`stats-event-kpi-legend ${safeItems.length === 2 ? 'is-compact' : ''}`}>
                {safeItems.map((item) => (
                    <div
                        key={`event-kpi-legend-${title}-${item.key}`}
                        className={`stats-event-kpi-legend-item ${activeItemKey === item.key ? 'is-hovered' : ''}`}
                    >
                        <span className="stats-event-kpi-legend-dot" style={{ backgroundColor: item.color }} />
                        <span className="stats-event-kpi-legend-label">{item.label}</span>
                        <span className="stats-event-kpi-legend-value">{item.count} - {item.percent}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const EventsMetricCard = ({
    title,
    metricLabel,
    items,
    barColor,
    expanded,
    onToggleExpand,
    onEventClick,
    toSafeMetricNumber,
}) => {
    const safeItems = (Array.isArray(items) ? items : [])
        .filter((item) => toSafeMetricNumber(item?.metricValue) != null);
    const visibleItems = expanded ? safeItems : safeItems.slice(0, 5);
    const maxMetricValue = safeItems.reduce(
        (maxValue, item) => Math.max(maxValue, toSafeMetricNumber(item?.metricValue) || 0),
        0
    );
    const normalizer = maxMetricValue > 0 ? maxMetricValue : 1;
    const canToggle = safeItems.length > 5 && typeof onToggleExpand === 'function';

    return (
        <div className="stats-chart-card stats-events-metric-card">
            <div className="stats-events-metric-card-header">
                <div className="stats-chart-title">{title}</div>
                {metricLabel ? <div className="stats-events-metric-label">{metricLabel}</div> : null}
            </div>

            <div className="stats-events-metric-list">
                {visibleItems.length > 0 ? (
                    visibleItems.map((item, index) => {
                        const metricValue = toSafeMetricNumber(item?.metricValue) || 0;
                        const barWidth = (metricValue / normalizer) * 100;
                        const location = [item?.place, item?.city].filter(Boolean).join(', ');

                        return (
                            <div
                                key={`events-metric-${item?.id ?? index}-${item?.name ?? index}`}
                                className="stats-events-metric-item"
                            >
                                <div className="stats-events-metric-item-header">
                                    {typeof onEventClick === 'function' && item?.id != null ? (
                                        <button
                                            type="button"
                                            className="stats-events-metric-item-name-button"
                                            onClick={() => onEventClick(item)}
                                            title="Przejdź do wydarzenia i pokaż szczegóły"
                                        >
                                            <span className="stats-events-metric-item-name">
                                                {item?.name || 'Brak danych'}
                                            </span>
                                        </button>
                                    ) : (
                                        <div className="stats-events-metric-item-name">{item?.name || 'Brak danych'}</div>
                                    )}
                                    <div className="stats-events-metric-item-value">{formatStatNumber(metricValue)}</div>
                                </div>
                                <div className="stats-events-metric-item-subtitle">{location || 'Brak danych'}</div>
                                <div className="stats-events-metric-item-track">
                                    <span
                                        className="stats-events-metric-item-fill"
                                        style={{
                                            width: `${barWidth}%`,
                                            backgroundColor: barColor,
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="stats-events-metric-empty">Brak danych dla wybranego zakresu.</div>
                )}
            </div>

            {canToggle && (
                <button type="button" className="stats-events-metric-toggle" onClick={onToggleExpand}>
                    {expanded ? 'Pokaż mniej' : 'Pokaż wszystkie'} <span>{expanded ? '▲' : '▼'}</span>
                </button>
            )}
        </div>
    );
};
